import { useState, useEffect } from 'react';
import { 
  Card, 
  CardBody, 
  CardHeader, 
  Spinner, 
  TableColumn,
  Table, 
  TableHeader, 
  TableBody, 
  TableRow, 
  TableCell,
  Pagination,
  Button,
  Input,
  Select,
  SelectItem,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem
} from '@heroui/react';
import type { UserInvitation } from '@/types/user-invitation.model';
import { listEmpresaInvitations, deleteEmpresaInvitation } from '@/services/empresa/users.service';
import InvitationModal from './InvitationModal';
import InvitationDetailModal from './InvitationDetailModal';

interface InvitationListProps {}

const InvitationList = ({}: InvitationListProps) => {
  const [invitations, setInvitations] = useState<UserInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Filtering and sorting state
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('email');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  
  // Modals and operations state
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isInvitationModalOpen, setIsInvitationModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState<UserInvitation | null>(null);

  // Fetch invitations
  useEffect(() => {
    const fetchInvitations = async () => {
      try {
        setLoading(true);
        const response = await listEmpresaInvitations(
          currentPage,
          itemsPerPage
        );
        
        setInvitations(response.data);
        setTotalItems(response.meta.pagination.total);
        setTotalPages(response.meta.pagination.pageCount);
        setError(null);
      } catch (err) {
        setError('Error al cargar las invitaciones');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchInvitations();
    return () => { setInvitations([]); }
  }, [currentPage, itemsPerPage]);

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Handle items per page change
  const handleItemsPerPageChange = (value: string) => {
    const numValue = Number(value);
    if (!isNaN(numValue)) {
      setItemsPerPage(numValue);
      setCurrentPage(1); // Reset to first page when changing items per page
    }
  };

  // Handle sort change
  const handleSortChange = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Handle search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle deleting an invitation
  const handleDeleteInvitation = async (documentId: string) => {
    setDeletingId(documentId);
    try {
      await deleteEmpresaInvitation(documentId);
      
      // Refresh the list of invitations
      const response = await listEmpresaInvitations(
        currentPage,
        itemsPerPage
      );
      
      setInvitations(response.data);
      setTotalItems(response.meta.pagination.total);
      setTotalPages(response.meta.pagination.pageCount);
    } catch (err) {
      setError('Error al eliminar la invitación');
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  };

  // Handle invitation submission
  const handleInvitationSubmit = (success: boolean) => {
    if (success) {
      // Refresh the list of invitations
      const fetchInvitations = async () => {
        try {
          setLoading(true);
          const response = await listEmpresaInvitations(
            currentPage,
            itemsPerPage
          );
          
          setInvitations(response.data);
          setTotalItems(response.meta.pagination.total);
          setTotalPages(response.meta.pagination.pageCount);
          setError(null);
        } catch (err) {
          setError('Error al cargar las invitaciones');
          console.error(err);
        } finally {
          setLoading(false);
        }
      };

      fetchInvitations();
    }
  };

  // Calculate sort indicator
  const getSortIndicator = (field: string) => {
    if (sortBy !== field) return '';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="w-full">
      <Card className="w-full">
        <CardHeader className="border-b flex flex-direction-row justify-between items-center p-6">
          <h3 className="text-xl font-bold">Usuarios asociados a la Empresa</h3>          
          <p className="text-default-500">Gestiona los usuarios de tu empresa</p>
        </CardHeader>
        <CardBody className="p-6">
          {/* Filters and Controls */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
              <div className="relative w-full md:w-64">
                <Input
                  placeholder="Buscar invitaciones..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="pl-10"
                />
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              
              <Select 
                aria-label="Elementos por página"
                className="w-[180px]"
                selectedKeys={[itemsPerPage.toString()]}
                onSelectionChange={(keys) => {
                  const value = Array.from(keys).join('');
                  if (value) handleItemsPerPageChange(value);
                }}
              >
                <SelectItem key="5">5 por página</SelectItem>
                <SelectItem key="10">10 por página</SelectItem>
                <SelectItem key="20">20 por página</SelectItem>
                <SelectItem key="50">50 por página</SelectItem>
              </Select>
            </div>
            
            <Button 
              className="w-full md:w-auto"
              onPress={() => setIsInvitationModalOpen(true)}
            >
              <svg
                className="mr-2 h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              Nueva Invitación
            </Button>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-center text-red-500">
                <p>{error}</p>
                <Button 
                  variant="bordered" 
                  onPress={() => window.location.reload()}
                  className="mt-4"
                >
                  Reintentar
                </Button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="w-full">
                <TableHeader>
                  <TableColumn key="acciones" className="px-3 py-2 text-left">Acciones</TableColumn>
                  <TableColumn 
                    key="nombre"
                    onClick={() => handleSortChange('nombre')}
                    allowsSorting
                  >
                    Nombre {getSortIndicator('nombre')}
                  </TableColumn>
                  <TableColumn 
                    key="email"                      
                    onClick={() => handleSortChange('email')}
                    allowsSorting
                  >
                    Email {getSortIndicator('email')}
                  </TableColumn>
                  <TableColumn 
                    key="rol"                      
                    onClick={() => handleSortChange('rol')}
                    allowsSorting
                  >
                    Rol {getSortIndicator('rol')}
                  </TableColumn>
                  <TableColumn 
                    key="estado"                      
                    onClick={() => handleSortChange('estado')}
                    allowsSorting
                  >
                    Estado {getSortIndicator('estado')}
                  </TableColumn>
                  <TableColumn 
                    key="fechaCreacion"                      
                    onClick={() => handleSortChange('createdAt')}
                    allowsSorting
                  >
                    Fecha de Creación {getSortIndicator('createdAt')}
                  </TableColumn>
                </TableHeader>
                <TableBody
                  isLoading={loading}
                  loadingContent={<Spinner label="Loading..." />}
                  items={invitations}
                >
                  {invitations.length > 0 ? (
                    invitations.map((invitation) => (
                      <TableRow key={invitation.id}>
                        <TableCell className="text-left">
                          <Dropdown>
                            <DropdownTrigger>
                              <Button variant="light" className="h-8 w-8 p-0">
                                <span className="sr-only">Abrir menú</span>
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                                  />
                                </svg>
                              </Button>
                            </DropdownTrigger>
                            <DropdownMenu aria-label="Acciones de la invitación">
                              <DropdownItem 
                                key="view-detail" 
                                onClick={() => {
                                  setSelectedInvitation(invitation);
                                  setIsDetailModalOpen(true);
                                }}
                              >
                                <svg
                                  className="mr-2 h-4 w-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                  />
                                </svg>
                                Ver Detalle
                              </DropdownItem>
                              <DropdownItem 
                                key="resend" 
                                onClick={() => {
                                  console.log('Reenviar invitación:', invitation.id);
                                }}
                              >
                                <svg
                                  className="mr-2 h-4 w-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                  />
                                </svg>
                                Reenviar
                              </DropdownItem>
                              <DropdownItem 
                                key="delete" 
                                className="text-danger"
                                color="danger"
                                onClick={() => invitation.id && handleDeleteInvitation(invitation?.documentId ?? "")}
                                isDisabled={deletingId === invitation.documentId}
                              >
                                {deletingId === invitation.documentId ? (
                                  <div className="flex items-center">
                                    <svg
                                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-danger"
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                    >
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                      ></circle>
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                      ></path>
                                    </svg>
                                    Eliminando...
                                  </div>
                                ) : (
                                  <>
                                    <svg
                                      className="mr-2 h-4 w-4"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                      />
                                    </svg>
                                    Eliminar
                                  </>
                                )}
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        </TableCell>
                        <TableCell className="font-medium">{invitation.nombre}</TableCell>
                        <TableCell>{invitation.email}</TableCell>
                        <TableCell>{invitation.role || 'N/A'}</TableCell>
                        <TableCell>
                          <span className={
                            invitation.estado === 'pending' 
                              ? 'bg-warning-500 text-white px-2 py-1 rounded text-sm' 
                              : 'bg-success-500 text-white px-2 py-1 rounded text-sm'
                          }>
                            {invitation.estado || 'N/A'}
                          </span>
                        </TableCell>
                        <TableCell>
                          {invitation.createdAt ? new Date(invitation.createdAt).toLocaleDateString() : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-10">
                        No se encontraron invitaciones
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && !loading && (
            <div className="flex flex-col sm:flex-row items-center justify-between pt-6">
              <div className="text-sm text-default-500 mb-4 sm:mb-0">
                Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems} resultados
              </div>
              <Pagination
                total={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                showControls
              />
            </div>
          )}
        </CardBody>
      </Card>
      
      {/* Invitation Modal */}
      <InvitationModal 
        isOpen={isInvitationModalOpen}
        onClose={() => setIsInvitationModalOpen(false)}
        onSubmit={handleInvitationSubmit}
      />
      
      {/* Invitation Detail Modal */}
      <InvitationDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        invitation={selectedInvitation}
        onResend={(invitation: UserInvitation) => {
          console.log('Resending invitation:', invitation);
        }}
        onDelete={async (invitation: UserInvitation) => {
          if (invitation.documentId) {
            await handleDeleteInvitation(invitation.documentId);
          }
        }}
      />
    </div>
  );
};

export default InvitationList;